// Obliczenia dla modułu "Raport czasów odpraw".
// Wszystkie daty z backendu to tekst "YYYY-MM-DD HH:mm:ss" w czasie warszawskim;
// parsujemy je jako UTC, bo liczą się tylko różnice między nimi.

export const STATUSY = [
  "Do przygotowania",
  "Do weryfikacji",
  "Wstępnie przygotowane",
  "Do wysłania",
  "Wysłane",
  "Odprawa zakończona",
];

// Statusy, na które wpływ ma agencja (przed wysłaniem zgłoszenia).
export const STATUSY_AGENCJI = STATUSY.slice(0, 4);

const KOLORY_STATUSOW = {
  "Do przygotowania": "#3B6FB6",
  "Do weryfikacji": "#8A63B8",
  "Wstępnie przygotowane": "#2E9C8F",
  "Do wysłania": "#D08A2E",
  Wysłane: "#C7544D",
  "Odprawa zakończona": "#6F7F90",
};

export const kolorStatusu = (status) => KOLORY_STATUSOW[status] || "#AEB6BF";

export const ROLE = {
  osobaPrzygotowujacaOdprawe: "osoby przygotowującej",
  osobaWysylajacaOdprawe: "osoby wysyłającej",
};

export const NAZWY_POL = {
  statusRealizacji: "status",
  osobaPrzygotowujacaOdprawe: "osoba",
  lokalizacjaTowaru: "lokalizacja",
  rodzajTransportu: "transport",
};

export const WSZYSCY = "__wszyscy__";
export const WSZYSTKIE = "__wszystkie__";

// Scenariusze odprawy względem przyjazdu towaru (auto / kurier) na miejsce odprawy.
// We wszystkich czas pracy agenta kończy się na statusie "Wysłane".
export const SCENARIUSZE = {
  na_miejscu: {
    nazwa: "Towar na miejscu od utworzenia",
    krotko: "na miejscu",
    opis: "Odprawa utworzona od razu jako Na Kołach (lub Kurier MU… / Magazyn Warszawa). Praca agenta od utworzenia.",
  },
  czekala: {
    nazwa: "Czekała na przyjazd",
    krotko: "czekała na przyjazd",
    opis: "Przyjazd po utworzeniu, przed wysłaniem. Praca agenta od przyjazdu.",
  },
  przed_przyjazdem: {
    nazwa: "Wysłana przed przyjazdem (do sprawdzenia)",
    krotko: "przed przyjazdem",
    opis: "Zgłoszenie wysłane, zanim oznaczono przyjazd (Na Kołach / lokalizacja). Zwykle przy wysyłce w drodze zaznacza się Na Kołach, więc to prawdopodobnie brak oznaczenia. Praca agenta od utworzenia, wliczona do mediany.",
  },
  bez_przyjazdu: {
    nazwa: "Bez informacji o przyjeździe",
    krotko: "bez przyjazdu",
    opis: "Towar w drodze / u kuriera i brak zmiany na przyjazd. Praca agenta od utworzenia.",
  },
};

const norm = (v) => String(v ?? "").trim().toLowerCase();

// Rodzaj transportu: "W drodze" i "Kurier" znaczą, że towaru jeszcze nie ma.
// Każda inna wartość (Na Kołach, Kurier MU MAN zakład, Kurier MU VOLKSWAGEN…, Magazyn
// Warszawa) oznacza towar na miejscu; "Zakończono" to koniec procesu, nie przyjazd.
const TRANSPORT_W_DRODZE = new Set(["w drodze", "kurier", ""]);
const TRANSPORT_NEUTRALNY = new Set(["zakończono"]);
const transportNaMiejscu = (v) =>
  !TRANSPORT_W_DRODZE.has(norm(v)) && !TRANSPORT_NEUTRALNY.has(norm(v));

// Lokalizacja towaru: "W DRODZE" i "NIEZNANA" (lub brak) to brak towaru na miejscu,
// każda konkretna lokalizacja (np. "MU MAN zakład", "OC Tychy") to towar na miejscu.
const LOKALIZACJA_W_DRODZE = new Set(["w drodze", "nieznana", ""]);
const lokalizacjaNaMiejscu = (v) => !LOKALIZACJA_W_DRODZE.has(norm(v));

const toMs = (s) => (s ? Date.parse(String(s).replace(" ", "T") + "Z") : null);

const roznicaMin = (a, b) => {
  if (a == null || b == null || Number.isNaN(a) || Number.isNaN(b)) return null;
  const m = Math.round((b - a) / 6000) / 10;
  return m < 0 ? null : m;
};

export const formatCzas = (m) => {
  if (m == null || Number.isNaN(m)) return "—";
  if (m < 1) return `${Math.round(m * 60)} s`;
  if (m < 60) return `${Math.round(m)} min`;
  const r = Math.round(m);
  if (r < 1440) return `${Math.floor(r / 60)} h ${r % 60} min`;
  return `${Math.floor(r / 1440)} d ${Math.floor((r % 1440) / 60)} h`;
};

export const formatData = (s) =>
  s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)} ${s.slice(11, 16)}` : "—";

export const mediana = (wartosci) => {
  const v = wartosci.filter((x) => x != null).sort((a, b) => a - b);
  if (!v.length) return null;
  const m = v.length >> 1;
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
};

export const kwantyl = (wartosci, q) => {
  const v = wartosci.filter((x) => x != null).sort((a, b) => a - b);
  if (!v.length) return null;
  const pos = (v.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return v[lo] + (v[hi] - v[lo]) * (pos - lo);
};

// Przycina pobyty w statusach do okresu od startu pracy agenta (pobyt rozpoczęty przed
// przyjazdem liczy się tylko od przyjazdu).
const przytnijPobyty = (pobyty, startMs) =>
  pobyty
    .map((p) => {
      const od = Math.max(toMs(p.od), startMs);
      const doMs = toMs(p.do);
      return { ...p, minuty: doMs > od ? Math.round((doMs - od) / 6000) / 10 : 0 };
    })
    .filter((p) => p.minuty > 0);

const sumujPoStatusie = (pobyty) => {
  const wynik = {};
  for (const p of pobyty) wynik[p.status] = (wynik[p.status] || 0) + p.minuty;
  return wynik;
};

// Łączy odprawy z ich historią i liczy czasy:
// - przyjazd: najwcześniejszy sygnał, że towar dotarł na miejsce odprawy:
//     rodzaj transportu "W drodze"/"Kurier" -> Na Kołach / Kurier MU… / Magazyn Warszawa,
//     lub lokalizacja "W DRODZE"/"NIEZNANA" -> konkretna lokalizacja,
// - scenariusz (patrz SCENARIUSZE) i start pracy agenta (przyjazd albo utworzenie),
// - minAgent: start pracy agenta -> "Wysłane" (główna miara),
// - minCzekanieNaPrzyjazd: utworzenie -> przyjazd,
// - minPrzygotowanie: utworzenie -> "Wysłane" (miara pomocnicza),
// - minUrzad: "Wysłane" -> "Odprawa zakończona", minCalosc: utworzenie -> "Zrealizowane",
// - pobyty / czasWStatusach: pełna historia statusów od utworzenia,
// - pobytyAgenta / czasWStatusachAgenta: tylko od startu pracy agenta.
export function zbudujOdprawy(odprawy, zmiany) {
  const poId = new Map();
  for (const z of zmiany) {
    if (!poId.has(z.customId)) poId.set(z.customId, []);
    poId.get(z.customId).push(z);
  }

  return odprawy.map((o) => {
    const historia = poId.get(o.id) || [];
    const statusy = historia.filter((h) => h.fieldName === "statusRealizacji");
    const transport = historia.filter((h) => h.fieldName === "rodzajTransportu");
    const lokalizacje = historia.filter((h) => h.fieldName === "lokalizacjaTowaru");
    const utworzono = toMs(o.utworzono);

    const pobyty = [];
    let od = o.utworzono;
    for (const z of statusy) {
      pobyty.push({
        status: z.oldValue || "—",
        nastepny: z.newValue,
        od,
        do: z.editedAt,
        minuty: roznicaMin(toMs(od), toMs(z.editedAt)) ?? 0,
        kto: z.editedBy,
      });
      od = z.editedAt;
    }

    const pierwszaZmianaNa = (na) => {
      const e = statusy.find((z) => z.newValue === na);
      return e ? toMs(e.editedAt) : null;
    };
    const wyslane = pierwszaZmianaNa("Wysłane") ?? toMs(o.dataStatusWyslane);
    const zakonczona = pierwszaZmianaNa("Odprawa zakończona");
    const zrealizowane = pierwszaZmianaNa("Zrealizowane");

    // Sygnały przyjazdu (z historii; dla transportu bez historii - dane.dataZmianyNaKolach).
    const sygnaly = [
      ...transport
        .filter((h) => TRANSPORT_W_DRODZE.has(norm(h.oldValue)) && transportNaMiejscu(h.newValue))
        .map((h) => ({ kiedy: h.editedAt, zrodlo: "transport", opis: `${h.oldValue || "—"} → ${h.newValue}`, kto: h.editedBy })),
      ...lokalizacje
        .filter((h) => !lokalizacjaNaMiejscu(h.oldValue) && lokalizacjaNaMiejscu(h.newValue))
        .map((h) => ({ kiedy: h.editedAt, zrodlo: "lokalizacja", opis: `${h.oldValue || "—"} → ${h.newValue}`, kto: h.editedBy })),
    ];
    if (!transport.length && o.dataZmianyNaKolach) {
      sygnaly.push({ kiedy: o.dataZmianyNaKolach, zrodlo: "transport", opis: "→ Na Kołach", kto: null });
    }
    sygnaly.sort((a, b) => String(a.kiedy).localeCompare(String(b.kiedy)));

    // Stan przy utworzeniu: pierwsza wartość "z" w historii transportu, a bez historii -
    // obecna wartość (chyba że jest dane.dataZmianyNaKolach, czyli auto przyjechało później).
    // Odprawa utworzona od razu jako "Na Kołach" (lub Kurier MU… / Magazyn Warszawa) ma
    // towar na miejscu od utworzenia - późniejsze zmiany lokalizacji nie przesuwają startu.
    // Sama lokalizacja przy utworzeniu (np. "OC Tychy" przy "W drodze") to miejsce docelowe,
    // więc decyduje tylko wtedy, gdy rodzaj transportu jest pusty.
    const transportStart = transport.length ? transport[0].oldValue : o.rodzajTransportu;
    const lokalizacjaStart = lokalizacje.length ? lokalizacje[0].oldValue : o.lokalizacjaTowaru;
    const naMiejscuPrzyUtworzeniu =
      !(!transport.length && o.dataZmianyNaKolach) &&
      (norm(transportStart)
        ? transportNaMiejscu(transportStart) || TRANSPORT_NEUTRALNY.has(norm(transportStart))
        : lokalizacjaNaMiejscu(lokalizacjaStart));

    // Przyjazd = najwcześniejszy sygnał po utworzeniu (transport albo lokalizacja).
    const przyjazd = naMiejscuPrzyUtworzeniu
      ? null
      : sygnaly.find((s) => toMs(s.kiedy) > utworzono) || null;
    const przyjazdMs = przyjazd ? toMs(przyjazd.kiedy) : null;

    let scenariusz;
    if (naMiejscuPrzyUtworzeniu) scenariusz = "na_miejscu";
    else if (przyjazdMs == null) scenariusz = "bez_przyjazdu";
    else if (wyslane != null && przyjazdMs > wyslane) scenariusz = "przed_przyjazdem";
    else scenariusz = "czekala";

    const startAgenta = scenariusz === "czekala" ? przyjazdMs : utworzono;
    const pobytyAgenta = przytnijPobyty(pobyty, startAgenta);

    const pierwszePrzygotowanie = statusy.find((z) =>
      ["Wstępnie przygotowane", "Do wysłania"].includes(z.newValue),
    );
    const przygotowanePrzedPrzyjazdem =
      scenariusz === "czekala" && pierwszePrzygotowanie != null
        ? toMs(pierwszePrzygotowanie.editedAt) <= przyjazdMs
        : null;

    return {
      ...o,
      historia,
      pobyty,
      czasWStatusach: sumujPoStatusie(pobyty),
      pobytyAgenta,
      czasWStatusachAgenta: sumujPoStatusie(pobytyAgenta),
      scenariusz,
      przyjazd,
      startAgentaOd: scenariusz === "czekala" ? `przyjazdu (${przyjazd.zrodlo})` : "utworzenia",
      przygotowanePrzedPrzyjazdem,
      minAgent: roznicaMin(startAgenta, wyslane),
      minCzekanieNaPrzyjazd: przyjazdMs != null ? roznicaMin(utworzono, przyjazdMs) : null,
      minPodjecie: statusy.length ? roznicaMin(utworzono, toMs(statusy[0].editedAt)) : null,
      minPrzygotowanie: roznicaMin(utworzono, wyslane),
      minUrzad: roznicaMin(wyslane, zakonczona),
      minCalosc: roznicaMin(utworzono, zrealizowane),
      minSuma: pobyty.reduce((a, p) => a + p.minuty, 0),
      minSumaAgenta: pobytyAgenta.reduce((a, p) => a + p.minuty, 0),
    };
  });
}

export function filtruj(odprawy, { uzytkownik, rola, rodzaj, scenariusz = WSZYSTKIE }) {
  return odprawy.filter(
    (o) =>
      (uzytkownik === WSZYSCY || o[rola] === uzytkownik) &&
      (rodzaj === WSZYSTKIE || o.rodzaj === rodzaj) &&
      (scenariusz === WSZYSTKIE || o.scenariusz === scenariusz),
  );
}

export function podsumuj(odprawy) {
  const czekaly = odprawy.filter((o) => o.scenariusz === "czekala");
  const scenariusze = {};
  Object.keys(SCENARIUSZE).forEach((k) => {
    scenariusze[k] = odprawy.filter((o) => o.scenariusz === k).length;
  });
  return {
    liczba: odprawy.length,
    agent: mediana(odprawy.map((o) => o.minAgent)),
    czekanie: mediana(czekaly.map((o) => o.minCzekanieNaPrzyjazd)),
    przygotowanePrzed: czekaly.filter((o) => o.przygotowanePrzedPrzyjazdem).length,
    czekalyLiczba: czekaly.length,
    scenariusze,
    podjecie: mediana(odprawy.map((o) => o.minPodjecie)),
    przygotowanie: mediana(odprawy.map((o) => o.minPrzygotowanie)),
    urzad: mediana(odprawy.map((o) => o.minUrzad)),
    calosc: mediana(odprawy.map((o) => o.minCalosc)),
  };
}

// Mediana łącznego czasu w każdym statusie (liczona po odprawach, które w nim były).
// klucz: "czasWStatusachAgenta" (od startu pracy agenta) albo "czasWStatusach" (od utworzenia).
export function medianyStatusow(odprawy, klucz = "czasWStatusachAgenta") {
  const wynik = {};
  const wszystkie = new Set(STATUSY);
  odprawy.forEach((o) => Object.keys(o[klucz]).forEach((s) => wszystkie.add(s)));
  for (const s of wszystkie) {
    const v = odprawy.map((o) => o[klucz][s]).filter((x) => x != null);
    if (v.length) wynik[s] = { mediana: mediana(v), liczba: v.length };
  }
  return wynik;
}

// miara: "minAgent" (od startu pracy agenta) albo "minPrzygotowanie" (od utworzenia).
export function statystykiOsob(odprawy, rola, miara = "minAgent") {
  const grupy = new Map();
  for (const o of odprawy) {
    const u = o[rola];
    if (!u) continue;
    if (!grupy.has(u)) grupy.set(u, []);
    grupy.get(u).push(o);
  }
  return [...grupy.entries()]
    .map(([uzytkownik, lista]) => ({
      uzytkownik,
      liczba: lista.length,
      liczbaZMiara: lista.filter((o) => o[miara] != null).length,
      wartosc: mediana(lista.map((o) => o[miara])),
    }))
    .filter((r) => r.wartosc != null)
    .sort((a, b) => a.wartosc - b.wartosc);
}
